import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { eventTypeLabel } from "../../shared/event-types";
import { evaluationLabel } from "../../shared/tasting";
import { STAGE_LABELS, type SalesStage, hasPermission } from "../../shared/permissions";
import { LABEL_ADULTS, LABEL_CHILDREN_FREE, LABEL_CHILDREN_HALF } from "../../shared/child-price";
import { cellText, nameParts } from "../../shared/workbook";
import { api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf, onSubmit } from "./ui";

type Suggestion = { id: string; field_key: string; suggested_value: string | null; uncertainty: string; review_status: string; evidence: string | null };
type EventFile = { id: string; filename: string; mime: string | null; size: number | null; visibility: string; created_at: string };
type EventDetail = {
  event: { id: string; title: string; event_type: string | null; sales_stage: SalesStage; version: number; notes: string | null; staff_notes: string | null; area: string | null; start_date: string | null; date_label: string | null; adults: number | null; children: number | null; date_precision: string };
  contact: { full_name: string; given_name: string | null; family_name: string | null; phone: string | null; email: string | null } | null;
  venue: { name: string } | null;
  workbook: Record<string, string>;
  sessions: { id: string; name: string }[];
  dietary: { id: string; requirement: string; count: number | null; source: string | null }[];
  tasks: { id: string; title: string; status: string; due_at: string | null }[];
  communications: { id: string; direction: string; channel: string; subject: string | null }[];
  proposals: { id: string; number: string; status: string }[];
  suggestions: Suggestion[];
  files: EventFile[];
  tastings?: { id: string; tasted_on: string | null; revisit_on: string | null; evaluation: string | null; evaluation_note: string | null }[];
};

export function EventPage() {
  const { id = "" } = useParams();
  const { user } = useSession();
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [matches, setMatches] = useState<{ venue: { name: string }; availability: { state: string; detail: string }; blockers: string[] }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const canWrite = hasPermission(user.role, user.permissions, "events.write");
  const canPropose = hasPermission(user.role, user.permissions, "proposals.write");

  function load() {
    api<EventDetail>(`/api/events/${id}`)
      .then(setDetail)
      .catch((reason: unknown) => setError(messageOf(reason)));
    api<{ results: { venue: { name: string }; availability: { state: string; detail: string }; blockers: string[] }[] }>(`/api/events/${id}/matches`)
      .then((result) => setMatches(result.results))
      .catch(() => setMatches([]));
  }

  useEffect(load, [id]);

  if (!detail && !error) return <p>Φόρτωση εκδήλωσης…</p>;

  return (
    <>
      <header className="page-head">
        <div>
          <p className="meta"><Link to="/pipeline">Εκδηλώσεις</Link></p>
          <h1>{detail?.event.title}</h1>
          <p className="lede">{detail ? STAGE_LABELS[detail.event.sales_stage] : ""} · {detail?.event.date_precision === "exact" ? detail.event.start_date : detail?.event.date_label || "Αγνωστη ημερομηνία"}{detail ? guestSuffix(detail) : ""}</p>
        </div>
        <div className="actions">
        <Link className="button" to={`/events/${id}/tasting`}>Γευσιγνωσία</Link>
        {canWrite ? <Link className="button secondary" to={`/events/${id}/edit`}>Επεξεργασία</Link> : null}
        {canPropose ? (
          <button
            type="button"
            onClick={() => {
              setError(null);
              void api<{ id: string }>(`/api/events/${id}/proposals`, { method: "POST" })
                .then((created) => {
                  setNote("Ετοιμάστηκε πρόχειρη πρόταση με τους τρέχοντες κανόνες.");
                  window.location.assign(`/proposals/${created.id}`);
                })
                .catch((reason: unknown) => setError(messageOf(reason)));
            }}
          >
            Τιμή σε πρόχειρο
          </button>
        ) : null}
        </div>
      </header>
      <Banner error={error} note={note} />
      {detail ? (
        <>
        <div className="crm-sections">
          <CrmBlock title="Γαλάζια ενότητα · Ραντεβού και αξιολόγηση" rows={[
            ["Αύξων αριθμός", detail.workbook.serial || ""],
            ["Ονομα", nameParts(detail.contact?.full_name || detail.event.title, detail.contact?.given_name, detail.contact?.family_name).given_name],
            ["Επωνυμο", nameParts(detail.contact?.full_name || detail.event.title, detail.contact?.given_name, detail.contact?.family_name).family_name],
            ["Ωρα επίσκεψης", cellText("visit_time", detail.workbook.visit_time)],
            ["Ημερομηνία επίσκεψης", cellText("visit_date", detail.workbook.visit_date) || cellText("came", detail.workbook.came)],
            ["Ατομα στη δοκιμή", detail.workbook.tasting_guests || ""],
            ["Αρ. τραπεζιού", detail.workbook.table || ""],
            ["Επαναληπτική επίσκεψη", detail.tastings?.[0]?.revisit_on ?? ""],
            ["Υπεύθυνος πωλήσεων", detail.workbook.sales || ""],
            ["Επιθυμητή ημερομηνία", cellText("wanted_date", detail.workbook.wanted_date)],
            ["Επιθυμητός τύπος", typeLabel(detail.event.event_type)],
            [LABEL_ADULTS, detail.event.adults != null ? String(detail.event.adults) : ""],
            [LABEL_CHILDREN_HALF, detail.event.children != null ? String(detail.event.children) : ""],
            [LABEL_CHILDREN_FREE, detail.workbook.children_free || ""],
            ["Επιθυμητός χώρος", houseLabel(detail.workbook.wanted_venue)],
            ["Τηλέφωνο", detail.contact?.phone || ""],
            ["Email", detail.contact?.email || ""],
            ["Αξιολόγηση", evaluationText(detail)],
            ["Εκλεισε", detail.workbook.closed || ""],
            ["Επόμενη επικοινωνία", cellText("follow_up", detail.workbook.follow_up)],
            ["Σημειώσεις και συνημμένα", detail.workbook.status_note || ""],
            ["OK", detail.workbook.ok || ""],
            ["Mail που έστειλαν", cellText("mail_in", detail.workbook.mail_in)],
            ["Πότε απαντήσαμε", cellText("mail_out", detail.workbook.mail_out)],
          ]} />
          <CrmBlock title="Κόκκινη ενότητα · Δέσμευση και συμφωνία" rows={[
            ["Ημ. προφορικής δέσμευσης", cellText("commitment", detail.workbook.commitment)],
            ["Ημ. συμφωνητικού", cellText("contract_date", detail.workbook.contract_date)],
            ["Ημ. προκαταβολής", cellText("deposit_date", detail.workbook.deposit_date)],
            ["Οριστική ημερομηνία", cellText("agreed_date", detail.workbook.agreed_date) || (detail.event.date_precision === "exact" ? detail.event.start_date || "" : "")],
            ["Οριστικός τύπος", detail.workbook.agreed_type || ""],
            ["Οριστικός χώρος", detail.venue?.name || houseLabel(detail.workbook.agreed_venue)],
            ["Κωδικός φακέλου", detail.workbook.file_code || ""],
            ["Οριστικά άτομα", detail.workbook.agreed_guests || ""],
            ["1η δόση", detail.workbook.installment_1 || ""],
            ["2η δόση", detail.workbook.installment_2 || ""],
            ["3η δόση", detail.workbook.installment_3 || ""],
            ["Εξόφληση", detail.workbook.paid_off || ""],
            ["Τελικά ραντεβού", cellText("final_meeting", detail.workbook.final_meeting)],
          ]} />
          <CrmBlock title="Κίτρινη ενότητα · Λειτουργία εκδήλωσης" rows={[
            ["Χώρος πραγματοποίησης", houseLabel(detail.workbook.service_venue)],
            ["Ζώνη ώρας", detail.workbook.service_time || ""],
            ["Μενού", detail.workbook.menu || ""],
            ["Στήσιμο", detail.workbook.setup || ""],
            ["Ειδικές απαιτήσεις", detail.workbook.requirements || ""],
            ["DJ της επιχείρησης", detail.workbook.dj || ""],
            ["Ορχήστρα", detail.workbook.orchestra || ""],
            ["Πυροτεχνήματα", detail.workbook.fireworks || ""],
            ["Ανιματέρ", detail.workbook.animator || ""],
            ["Παιχνίδια", detail.workbook.games || ""],
            ["Άλλο", detail.workbook.extras || ""],
            ["Εκκλησία της επιχείρησης", detail.workbook.church || ""],
            ["Αλλεργίες", detail.workbook.allergies || ""],
            ["Λίστα καλεσμένων", detail.workbook.guest_list || ""],
            ["Τύπος εκδήλωσης", detail.workbook.service_type || ""],
            ["Σχόλια service", detail.workbook.service_notes || ""],
          ]} />
        </div>
        <article className="panel">
          <h2>Συνημμένα</h2>
          {(detail.files ?? []).length === 0 ? <p>Δεν υπάρχει αρχείο σε αυτή την εκδήλωση.</p> : (
            <ul>
              {detail.files.map((file) => (
                <li key={file.id} className="actions">
                  <a href={`/api/events/${id}/files/${file.id}`} download={file.filename}>{file.filename}</a>
                  {canWrite ? (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        setError(null);
                        void api(`/api/events/${id}/files/${file.id}`, { method: "DELETE" })
                          .then(load)
                          .catch((reason: unknown) => setError(messageOf(reason)));
                      }}
                    >
                      Αφαίρεση
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {canWrite ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const form = event.currentTarget;
                const data = new FormData(form);
                const chosen = data.get("file");
                if (!(chosen instanceof File) || !chosen.name) {
                  setError("Διάλεξε ένα αρχείο.");
                  return;
                }
                setError(null);
                void api(`/api/events/${id}/files`, { method: "POST", body: data })
                  .then(() => {
                    form.reset();
                    load();
                  })
                  .catch((reason: unknown) => setError(messageOf(reason)));
              }}
            >
              <div className="fields">
                <label>Αρχείο<input name="file" type="file" required accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods,.rtf" /></label>
              </div>
              <button type="submit">Μεταφόρτωση</button>
            </form>
          ) : null}
        </article>
        <div className="split">
          <section className="stack">
            <article className="panel">
              <h2>Απαιτήσεις</h2>
              <p>{detail.event.notes || "Δεν υπάρχουν σημειώσεις ακόμη."}</p>
              <p className="meta">Περιοχή {detail.event.area || "άγνωστη"}. Οι διατροφικές μετρήσεις επικαλύπτουν τους καλεσμένους. Δεν προστίθενται στον αριθμό ατόμων.</p>
              <ul>{detail.dietary.map((item) => <li key={item.id}>{item.requirement}: {item.count ?? "άγνωστο πλήθος"} · {item.source}</li>)}</ul>
              {canWrite ? (
                <form onSubmit={onSubmit(async (body) => {
                  await api(`/api/events/${id}/dietary`, { method: "POST", body: JSON.stringify({ requirement: body.requirement, count: Number(body.count) }) });
                  load();
                })}>
                  <div className="fields">
                    <label>Απαίτηση<input name="requirement" required /></label>
                    <label>Πλήθος<input name="count" inputMode="numeric" /></label>
                  </div>
                  <button type="submit">Προσθήκη διατροφικής σημείωσης</button>
                </form>
              ) : null}
            </article>
            <article className="panel">
              <h2>Προτάσεις συστήματος</h2>
              <p className="meta">Τίποτα δεν γράφεται στην εκδήλωση ώσπου να το αποδεχτείς. Το πρότυπο δεν επινοεί τιμή ή χώρο.</p>
              {detail.suggestions.length === 0 ? <p>Δεν υπάρχουν προτάσεις σε αυτή την εκδήλωση.</p> : null}
              {detail.suggestions.map((suggestion) => (
                <div key={suggestion.id} className="actions">
                  <span><strong>{suggestion.field_key}</strong> {suggestion.suggested_value} · {suggestion.uncertainty}</span>
                  {suggestion.review_status === "pending" && canWrite ? (
                    <button type="button" className="secondary" onClick={() => void api(`/api/suggestions/${suggestion.id}/accept`, { method: "POST" }).then(load).catch((reason: unknown) => setError(messageOf(reason)))}>Αποδοχή</button>
                  ) : <span className="meta">{suggestion.review_status}</span>}
                </div>
              ))}
            </article>
            <article className="panel">
              <h2>Μηνύματα και εργασίες</h2>
              {detail.communications.length === 0 ? <p>Δεν έχουν καταχωριστεί μηνύματα σε αυτή την εκδήλωση.</p> : null}
              <ul>{detail.communications.map((item) => <li key={item.id}>{item.direction} · {item.channel} · {item.subject || "Χωρίς θέμα"}</li>)}</ul>
              <ul>{detail.tasks.map((task) => <li key={task.id}>{task.title} · {task.status}{task.due_at ? ` · έως ${task.due_at}` : ""}{task.status === "open" ? <button type="button" className="secondary" onClick={() => void api(`/api/tasks/${task.id}/complete`, { method: "POST" }).then(load)}>Ετοιμο</button> : null}</li>)}</ul>
            </article>
          </section>
          <aside className="stack">
            <article className="panel">
              <h2>Προτάσεις</h2>
              {detail.proposals.length === 0 ? <p>Δεν υπάρχει πρόταση ακόμη.</p> : null}
              <ul>{detail.proposals.map((proposal) => <li key={proposal.id}><Link to={`/proposals/${proposal.id}`}>{proposal.number}</Link> · {proposal.status}</li>)}</ul>
            </article>
            <article className="panel">
              <h2>Ταίριασμα χώρων</h2>
              {matches.length === 0 ? <p>Δεν ετοιμάστηκε ταίριασμα.</p> : null}
              <ul>
                {matches.map((match) => (
                  <li key={match.venue.name}>
                    <strong>{match.venue.name}</strong> · <span className={`pill ${match.availability.state}`}>{match.availability.state}</span>
                    <div className="meta">{match.blockers[0] || match.availability.detail}</div>
                  </li>
                ))}
              </ul>
            </article>
          </aside>
        </div>
        </>
      ) : null}
    </>
  );
}

const HOUSES: Record<string, string> = { C: "Casa 28", K: "Κτήμα 28", R: "Regina 28", CASA: "Casa 28", KTHMA: "Κτήμα 28", REGINA: "Regina 28", "ΜΠ": "Μουσείο Ποδηλάτου", MP: "Μουσείο Ποδηλάτου" };

function houseLabel(value: string | undefined): string {
  if (!value) return "";
  const token = value.trim().toUpperCase().replace("Κ", "K");
  return HOUSES[token] || value;
}

function guestSuffix(detail: EventDetail): string {
  const parts: string[] = [];
  if (detail.event.adults != null) parts.push(`${detail.event.adults} ενήλικες`);
  if (detail.event.children != null && detail.event.children > 0) {
    const count = detail.event.children;
    parts.push(`${count} ${count === 1 ? "παιδί" : "παιδιά"} έως 11`);
  }
  const freeRaw = (detail.workbook.children_free ?? "").trim();
  const free = /^\d+$/.test(freeRaw) ? Number(freeRaw) : 0;
  if (free > 0) parts.push(`${free} ${free === 1 ? "παιδί" : "παιδιά"} έως 3,5`);
  return parts.length ? ` · ${parts.join(", ")}` : "";
}

function typeLabel(value: string | null): string {
  return eventTypeLabel(value);
}

function evaluationText(detail: EventDetail): string {
  const latest = detail.tastings?.[0];
  const code = latest ? latest.evaluation : detail.workbook.evaluation;
  const label = evaluationLabel(code);
  const note = latest?.evaluation === "OTHER" ? (latest.evaluation_note ?? "").trim() : "";
  if (label && note) return `${label}: ${note}`;
  return label;
}

function CrmBlock({ title, rows }: { title: string; rows: [string, string][] }) {
  const filled = rows.filter(([, value]) => value.trim());
  return (
    <article className="panel">
      <h2>{title}</h2>
      {filled.length === 0 ? <p>Τίποτα σε αυτή τη φάση.</p> : (
        <dl className="crm-fields">
          {filled.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl>
      )}
    </article>
  );
}
